import React from "react";
import PropTypes from "prop-types";
import { List, Grid, Box } from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import AddBoxSharpIcon from "@mui/icons-material/AddBoxSharp";
import IndeterminateCheckBoxSharpIcon from "@mui/icons-material/IndeterminateCheckBoxSharp";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import CountryMenuItemComponent from "./countryMenuItemComponent";
import { useGetModelsService } from "@hooks";
import { useStyles } from "./styles";
import { SelectedCountry } from "../../CityBar";

// Definición de PropTypes
export const CountryMenuItemPropTypes = {
  name: PropTypes.string.isRequired,
  link: PropTypes.string,
  acronym: PropTypes.string,
  cities: PropTypes.array,
  totalModels: PropTypes.number,
};

// Tipo de TypeScript inferido de PropTypes
type CountryMenuItemPropsType = PropTypes.InferProps<
  typeof CountryMenuItemPropTypes
>;

// Definición adicional de tipos
type CountryMenuItemPropsWithoutItems = Omit<
  CountryMenuItemPropsType,
  "cities"
>;

// Mejora en la declaración de los hijos
export type CountryMenuItemProps = CountryMenuItemPropsWithoutItems & {
  cities?: CountryMenuItemPropsType[]; // Nota: Asegúrate de que los tipos sean correctos
  children?: boolean;
  onSelect?: () => void; // 👈 call to close the drawer after selection
  soon?: boolean;
  onCountryClick?: (c: SelectedCountry) => void;
};

const CountryMenuItem: React.FC<CountryMenuItemProps> = (props) => {
  const {
    name,
    link,
    acronym,
    cities = [],
    children = false,
    totalModels = 0,
    onSelect,
    soon = false,
    onCountryClick,
  } = props;
  const classes = useStyles();
  const isExpandable = cities && cities.length > 0;
  const [open, setOpen] = React.useState(false);
  const { handleGetModelsByCity } = useGetModelsService();

  const handleClick = (name: string) => {
    if (!children) {
      onCountryClick?.({
        name,
        acronym: acronym ?? "",
        totalModels: totalModels ?? 0,
        soon,
        cities: (cities ?? []) as SelectedCountry["cities"],
      });
    }
    if (soon) {
      onSelect?.();
      return;
    }
    handleGetModelsByCity(name);
    onSelect?.();
  };

  const handleIconClick = (event: React.MouseEvent) => {
    if (isExpandable) setOpen(!open);
    event.stopPropagation();
  };

  const MenuItemRoot = (
    <CountryMenuItemComponent
      className={`${classes.menuItem} ${children ? "childBackground" : ""}`}
      link={link}
      onClick={() => handleClick(name)}
    >
      {!children && (
        <Box className={classes.row}>
          <span
            className={classes.plus}
            style={{
              opacity: isExpandable ? 1 : 0.25,
              cursor: isExpandable ? "pointer" : "default",
            }}
            onClick={handleIconClick}
          >
            {isExpandable && open ? (
              <IndeterminateCheckBoxSharpIcon fontSize="small" />
            ) : (
              <AddBoxSharpIcon fontSize="small" />
            )}
          </span>
          {!!acronym && (
            <span
              className={`fi fi-${acronym.toLowerCase()}`}
              style={{ fontSize: 16, flexShrink: 0 }}
            />
          )}
          <span
            className={classes.countryName}
            style={{ color: soon ? "#8a8a92" : "#fff" }}
          >
            {name}
          </span>
          <span className={classes.countryCount} style={{ color: "#9a9aa2" }}>
            {soon ? "Soon" : `(${totalModels})`}
          </span>
        </Box>
      )}
      {children && (
        <>
          <Grid item xs={10} className={classes.citySection}>
            <span className={classes.cityName}>{name}</span>
          </Grid>
          <Grid item xs={2} className={classes.countryCount}>
            ({totalModels})
          </Grid>
        </>
      )}

      {/* Display the expand menu if the item has children */}
    </CountryMenuItemComponent>
  );

  const MenuItemChildren = isExpandable ? (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Divider />
      <List component="div" disablePadding>
        {cities.map((item, index) => (
          <CountryMenuItem
            {...item}
            key={index}
            children={true}
            onSelect={onSelect}
          />
        ))}
      </List>
    </Collapse>
  ) : null;

  return (
    <>
      {MenuItemRoot}
      {MenuItemChildren}
    </>
  );
};

export default CountryMenuItem;
